// ═══════════════════════════════════════════════════════════
// PA Grants Store — Projects, Organizations, RFRs
// Server-side query pattern with pagination
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
// Note: fetchRecords is used for small reference tables (organizations, disasters)
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { uploadFileToV2, getV2FileDownloadUrl } from '../lib/uploadFile';
import type { PAProject, PAOrganization, PAReimbursementRequest, PADisaster } from '../types/pagrants';
import type { QueryOptions } from '../api/types';

const FP = FIELDS.paProjects;
const FO = FIELDS.paOrganizations;
const FR = FIELDS.paReimbursementRequests;
const FD = FIELDS.paDisasters;
const FPC = FIELDS.paProjectCosts;
const FDI = FIELDS.paDamageInventory;
const FIns = FIELDS.paInsurance;
const FNote = FIELDS.paNotes;
const FEq = FIELDS.paForceAccountEquipment;
const FMat = FIELDS.paMaterials;
const FCon = FIELDS.paContracts;
const FRent = FIELDS.paRentedEquipment;
const FDon = FIELDS.paDonatedResources;
const FComp = FIELDS.paCompliance;
const FTL = FIELDS.paTimelineEvents;
const FTE = FIELDS.paTimeExtensions;
const FDoc = FIELDS.paDocuments;

/** V2 may return reference fields as { record_id, display_value } objects — extract scalar ID */
function extractRef(val: unknown): string | null {
  if (!val && val !== 0) return null;
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>;
    const id = obj.record_id ?? obj.id ?? obj.value;
    return id != null ? String(id) : null;
  }
  return String(val);
}

function mapProject(r: Record<string, unknown>): PAProject {
  const title = (r[FP.title] as string) || '';
  const damageCategory = (r[FP.damageCategory] as string) || '';
  const siteDescription = (r[FP.siteDescription] as string) || '';
  const address = (r[FP.address] as string) || '';
  const city = (r[FP.city] as string) || '';
  const state = (r[FP.state] as string) || '';
  const zip = (r[FP.zip] as string) || '';
  const lat = (r[FP.latitude] as number) || null;
  const lng = (r[FP.longitude] as number) || null;
  const completionDeadline = (r[FP.completionDeadline] as string) || '';
  const id = r.record_id as number;
  return {
    id,
    Rid: id,
    projectNumber: (r[FP.projectNumber] as string) || '',
    title,
    // V1 compat aliases
    projectTitle: title,
    category: damageCategory,
    organizationId: extractRef(r[FP.organizationId]),
    disasterId: extractRef(r[FP.disasterId]),
    workType: (r[FP.workType] as string) || 'pa',
    damageCategory,
    phase: (r[FP.phase] as string) || 'formulation',
    status: (r[FP.status] as string) || 'draft',
    estimatedCost: String(Number(r[FP.estimatedCost]) || 0),
    obligatedAmount: String(Number(r[FP.obligatedAmount]) || 0),
    drawdownAmount: String(Number(r[FP.drawdownAmount]) || 0),
    federalSharePct: String(Number(r[FP.federalSharePct]) || 75),
    insuranceDeduction: String(Number(r[FP.insuranceDeduction]) || 0),
    mitigationCost: String(Number(r[FP.mitigationCost]) || 0),
    siteDescription,
    damageDescription: siteDescription,
    scopeOfWork: (r[FP.scopeOfWork] as string) || '',
    latitude: lat,
    longitude: lng,
    projectLat: String(lat || ''),
    projectLng: String(lng || ''),
    address,
    city,
    state,
    zip,
    projectAddress: address,
    projectCity: city,
    projectState: state,
    projectZip: zip,
    projectCounty: '',
    completionDeadline,
    deadline: completionDeadline,
    certificationDate: (r[FP.certificationDate] as string) || '',
    repairOrReplace: (r[FP.repairOrReplace] as string) || '',
    notes: (r[FP.notes] as string) || '',
    // Unmapped V1 fields — default to empty/zero so pages don't crash
    actualCost: '0',
    federalShareAmount: '0',
    applicantShareAmount: '0',
    netEligibleCost: '0',
    pendingReimbursement: '0',
    completionPctEstimate: '',
    completionDate: '',
    workStartDate: '',
    projectSize: '',
    costNarrative: '',
    hazardMitigationIncluded: '',
    hazardMitigationCost: '',
    hazardMitigationDescription: '',
    hazardMitigationBca: '',
    improvedProjectElection: '',
    improvedProjectDescription: '',
    preApprovalRequired: '',
    preApprovalStatus: '',
    extensionRequested: '',
    extensionNewDeadline: '',
    extensionJustification: '',
    versionNumber: '',
    assignedSpecialist: '',
    femaProjectId: '',
    cefRequired: '',
    certLegalResponsibility: '',
    certInsuranceCompliance: '',
    certProcurementCompliance: '',
    certNoWorkDuplication: '',
    certSignedBy: '',
    certSignedDate: '',
    creatorId: '',
    createdDate: '',
    lastModifiedBy: '',
    lastModifiedDate: '',
  } as any as PAProject;
}

function mapOrg(r: Record<string, unknown>): PAOrganization {
  const id = r.record_id as number;
  const femaId = (r[FO.femaId] as string) || '';
  const uei = (r[FO.uei] as string) || '';
  const phone = (r[FO.phone] as string) || '';
  const address = (r[FO.address] as string) || '';
  const city = (r[FO.city] as string) || '';
  const state = (r[FO.state] as string) || '';
  const zip = (r[FO.zip] as string) || '';
  return {
    id,
    Rid: id,
    orgName: (r[FO.orgName] as string) || '',
    orgType: (r[FO.orgType] as string) || '',
    orgRole: (r[FO.orgRole] as string) || '',
    fipsCode: (r[FO.fipsCode] as string) || '',
    femaId,
    uei,
    // V1 compat aliases — pages read these names
    femaApplicantId: femaId,
    ueiNumber: uei,
    primaryEmail: (r[FO.primaryEmail] as string) || '',
    contactEmails: (r[FO.contactEmails] as string) || '',
    phone,
    // primaryPhone alias — many pages read org.primaryPhone
    primaryPhone: phone,
    address,
    city,
    state,
    zip,
    // physicalAddress aliases — type requires these; no separate API fields exist
    physicalAddress: address,
    physicalCity: city,
    physicalState: state,
    physicalZip: zip,
    physicalCounty: '',
    mailingAddress: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    parentOrgId: String(r[FO.parentOrgId] || ''),
    status: (r[FO.status] as string) || 'active',
    notes: (r[FO.notes] as string) || '',
    // Fields required by type but with no V2 API key — default to empty string
    samRegistered: '', samExpirationDate: '',
    einTaxId: '', congressionalDistrict: '',
    primaryContact: '', primaryTitle: '',
    altContact: '', altTitle: '', altEmail: '', altPhone: '',
    garName: '', garEmail: '', garPhone: '',
    scoName: '', spaoName: '', spaoEmail: '',
    pnpType: '', pnpTaxExemptVerified: '', pnpTaxExemptLetter: '',
    pnpOpenToPublic: '', pnpServicesDescription: '', pnpCriticalFacility: '',
    procurementPolicyOnFile: '', forceAccountPolicyOnFile: '',
    indirectCostRate: '', indirectCostRateType: '',
    singleAuditRequired: '', singleAuditCurrent: '',
    bankName: '', bankRoutingNumber: '', bankAccountNumber: '', bankAccountType: '',
  } as any as PAOrganization;
}

function mapRFR(r: Record<string, unknown>): PAReimbursementRequest {
  const id = r.record_id as number;
  return {
    id, Rid: id,
    projectId: (r[FR.projectId] as number) || 0,
    organizationId: (r[FR.organizationId] as number) || null,
    requestNumber: (r[FR.requestNumber] as string) || '',
    requestedAmount: Number(r[FR.requestedAmount]) || 0,
    approvedAmount: Number(r[FR.approvedAmount]) || 0,
    federalShare: Number(r[FR.federalShare]) || 0,
    applicantMatch: Number(r[FR.applicantMatch]) || 0,
    status: (r[FR.status] as string) || 'draft',
    costBreakdown: (r[FR.costBreakdown] as string) || '',
    reviewerNotes: (r[FR.reviewerNotes] as string) || '',
    paymentRef: (r[FR.paymentRef] as string) || '',
    submittedBy: (r[FR.submittedBy] as string) || '',
    submittedAt: (r[FR.submittedAt] as string) || '',
    reviewedBy: (r[FR.reviewedBy] as string) || '',
    reviewedAt: (r[FR.reviewedAt] as string) || '',
  } as any as PAReimbursementRequest;
}

function mapDisaster(r: Record<string, unknown>): PADisaster {
  const id = r.record_id as number;
  return {
    id, Rid: id,
    disasterNumber: (r[FD.disasterNumber] as string) || '',
    disasterName: (r[FD.disasterName] as string) || '',
    declarationType: (r[FD.declarationType] as string) || '',
    declarationDate: (r[FD.declarationDate] as string) || '',
    incidentId: (r[FD.incidentId] as number) || null,
    incidentPeriodStart: (r[FD.incidentPeriodStart] as string) || '',
    incidentPeriodEnd: (r[FD.incidentPeriodEnd] as string) || '',
    federalSharePct: Number(r[FD.federalSharePct]) || 75,
    status: (r[FD.status] as string) || 'active',
    notes: (r[FD.notes] as string) || '',
  } as any as PADisaster;
}

function projectToPayload(rawData: Partial<PAProject>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  if (data.projectNumber !== undefined) p[FP.projectNumber] = data.projectNumber;
  if (data.title !== undefined) p[FP.title] = data.title;
  if (data.organizationId !== undefined) p[FP.organizationId] = data.organizationId;
  if (data.disasterId !== undefined) p[FP.disasterId] = data.disasterId;
  if (data.workType !== undefined) p[FP.workType] = data.workType;
  if (data.damageCategory !== undefined) p[FP.damageCategory] = data.damageCategory;
  if (data.phase !== undefined) p[FP.phase] = data.phase;
  if (data.status !== undefined) p[FP.status] = data.status;
  if (data.estimatedCost !== undefined) p[FP.estimatedCost] = data.estimatedCost;
  if (data.obligatedAmount !== undefined) p[FP.obligatedAmount] = data.obligatedAmount;
  if (data.drawdownAmount !== undefined) p[FP.drawdownAmount] = data.drawdownAmount;
  if (data.federalSharePct !== undefined) p[FP.federalSharePct] = data.federalSharePct;
  if (data.insuranceDeduction !== undefined) p[FP.insuranceDeduction] = data.insuranceDeduction;
  if (data.mitigationCost !== undefined) p[FP.mitigationCost] = data.mitigationCost;
  if (data.siteDescription !== undefined) p[FP.siteDescription] = data.siteDescription;
  if (data.scopeOfWork !== undefined) p[FP.scopeOfWork] = data.scopeOfWork;
  if (data.latitude !== undefined) p[FP.latitude] = data.latitude;
  if (data.longitude !== undefined) p[FP.longitude] = data.longitude;
  if (data.address !== undefined) p[FP.address] = data.address;
  if (data.city !== undefined) p[FP.city] = data.city;
  if (data.state !== undefined) p[FP.state] = data.state;
  if (data.zip !== undefined) p[FP.zip] = data.zip;
  if (data.completionDeadline !== undefined) p[FP.completionDeadline] = data.completionDeadline;
  // V1 compat aliases
  if (data.deadline !== undefined && data.completionDeadline === undefined) p[FP.completionDeadline] = data.deadline;
  if (data.projectTitle !== undefined && data.title === undefined) p[FP.title] = data.projectTitle;
  if (data.category !== undefined && data.damageCategory === undefined) p[FP.damageCategory] = data.category;
  if (data.damageDescription !== undefined && data.siteDescription === undefined) p[FP.siteDescription] = data.damageDescription;
  if (data.certificationDate !== undefined) p[FP.certificationDate] = data.certificationDate;
  if (data.repairOrReplace !== undefined) p[FP.repairOrReplace] = data.repairOrReplace;
  if (data.notes !== undefined) p[FP.notes] = data.notes;
  return p;
}

function orgToPayload(rawData: Partial<PAOrganization>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  if (data.orgName !== undefined) p[FO.orgName] = data.orgName;
  if (data.orgType !== undefined) p[FO.orgType] = data.orgType;
  if (data.orgRole !== undefined) p[FO.orgRole] = data.orgRole;
  // femaId and its V1 alias femaApplicantId map to the same field
  if (data.femaId !== undefined) p[FO.femaId] = data.femaId;
  if (data.femaApplicantId !== undefined && data.femaId === undefined) p[FO.femaId] = data.femaApplicantId;
  // uei and its V1 alias ueiNumber map to the same field
  if (data.uei !== undefined) p[FO.uei] = data.uei;
  if (data.ueiNumber !== undefined && data.uei === undefined) p[FO.uei] = data.ueiNumber;
  if (data.fipsCode !== undefined) p[FO.fipsCode] = data.fipsCode;
  if (data.primaryEmail !== undefined) p[FO.primaryEmail] = data.primaryEmail;
  if (data.contactEmails !== undefined) p[FO.contactEmails] = data.contactEmails;
  // phone and its alias primaryPhone map to the same field
  if (data.phone !== undefined) p[FO.phone] = data.phone;
  if (data.primaryPhone !== undefined && data.phone === undefined) p[FO.phone] = data.primaryPhone;
  if (data.address !== undefined) p[FO.address] = data.address;
  if (data.city !== undefined) p[FO.city] = data.city;
  if (data.state !== undefined) p[FO.state] = data.state;
  if (data.zip !== undefined) p[FO.zip] = data.zip;
  if (data.parentOrgId !== undefined) p[FO.parentOrgId] = data.parentOrgId;
  if (data.status !== undefined) p[FO.status] = data.status;
  if (data.notes !== undefined) p[FO.notes] = data.notes;
  return p;
}

interface PAGrantsState {
  // Projects
  projects: PAProject[];
  projectsTotal: number;
  projectsLoading: boolean;
  projectsPage: number;
  projectsSort: { field: string; dir: 'asc' | 'desc' };
  projectsFilters: Record<string, unknown>;
  activeDisasterId: string | null;
  setActiveDisasterId: (id: string | null) => void;
  fetchProjects: (opts?: Partial<QueryOptions>) => Promise<void>;
  setProjectsPage: (page: number) => void;
  setProjectsSort: (field: string, dir: 'asc' | 'desc') => void;

  // Organizations
  organizations: PAOrganization[];
  orgsLoading: boolean;
  fetchOrganizations: () => Promise<void>;

  // Disasters
  disasters: PADisaster[];
  fetchDisasters: () => Promise<void>;

  // Reimbursements
  reimbursements: PAReimbursementRequest[];
  rfrsTotal: number;
  rfrsLoading: boolean;
  fetchReimbursements: (opts?: Partial<QueryOptions>) => Promise<void>;

  // CRUD
  createProject: (data: Partial<PAProject>) => Promise<PAProject | null>;
  updateProject: (id: number, data: Partial<PAProject>) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  createOrganization: (data: Partial<PAOrganization>) => Promise<PAOrganization | null>;
  updateOrganization: (id: number, data: Partial<PAOrganization>) => Promise<void>;

  // V1 compat aliases
  fetchAll: () => Promise<void>;
  loadingProjects: boolean;
  loadingCosts: boolean;
  loadingDisasters: boolean;
  loadingOrgs: boolean;
  reimbursementRequests: any[];
  createReimbursement: (data: any) => Promise<number>;
  updateReimbursement: (id: any, data: any) => Promise<void>;
  deleteReimbursement: (id: any) => Promise<void>;
  damageItems: any[];
  fetchDamageItems: (disasterId?: number) => Promise<void>;
  createDamageItem: (data: any) => Promise<number>;
  updateDamageItem: (id: any, data: any) => Promise<void>;
  deleteDamageItem: (id: any) => Promise<void>;
  projectCosts: any[];
  fetchProjectCosts: (projectId?: number) => Promise<void>;
  createProjectCost: (data: any) => Promise<number>;
  updateProjectCost: (id: any, data: any) => Promise<void>;
  deleteProjectCost: (id: any) => Promise<void>;
  documents: any[];
  fetchDocuments: (projectId?: number) => Promise<void>;
  createDocument: (data: any) => Promise<number>;
  deleteDocument: (id: any) => Promise<void>;
  loadingDocs: boolean;
  loadingDamage: boolean;
  laborRecords: any[];
  fetchLabor: (projectId?: number) => Promise<void>;
  createLabor: (data: any) => Promise<number>;
  updateLabor: (id: any, data: any) => Promise<void>;
  deleteLabor: (id: any) => Promise<void>;
  equipRecords: any[];
  fetchEquipment: (projectId?: number) => Promise<void>;
  createEquip: (data: any) => Promise<number>;
  updateEquip: (id: any, data: any) => Promise<void>;
  deleteEquip: (id: any) => Promise<void>;
  materials: any[];
  fetchMaterials: (projectId?: number) => Promise<void>;
  createMaterial: (data: any) => Promise<number>;
  updateMaterial: (id: any, data: any) => Promise<void>;
  deleteMaterial: (id: any) => Promise<void>;
  contracts: any[];
  fetchContracts: (projectId?: number) => Promise<void>;
  createContract: (data: any) => Promise<number>;
  updateContract: (id: any, data: any) => Promise<void>;
  deleteContract: (id: any) => Promise<void>;
  rentedEquip: any[];
  fetchRented: (projectId?: number) => Promise<void>;
  createRented: (data: any) => Promise<number>;
  updateRented: (id: any, data: any) => Promise<void>;
  deleteRented: (id: any) => Promise<void>;
  donatedResources: any[];
  fetchDonated: (projectId?: number) => Promise<void>;
  createDonated: (data: any) => Promise<number>;
  updateDonated: (id: any, data: any) => Promise<void>;
  deleteDonated: (id: any) => Promise<void>;
  insurance: any[];
  fetchInsurance: (projectId?: number) => Promise<void>;
  createInsurance: (data: any) => Promise<number>;
  updateInsurance: (id: any, data: any) => Promise<void>;
  deleteInsurance: (id: any) => Promise<void>;
  compliance: any[];
  fetchCompliance: (projectId?: number) => Promise<void>;
  createCompliance: (data: any) => Promise<number>;
  updateCompliance: (id: any, data: any) => Promise<void>;
  deleteCompliance: (id: any) => Promise<void>;
  timelineEvents: any[];
  fetchTimeline: (projectId?: number) => Promise<void>;
  createTimeline: (data: any) => Promise<number>;
  createTimelineEvent: (data: any) => Promise<number>;
  updateTimelineEvent: (id: any, data: any) => Promise<void>;
  deleteTimelineEvent: (id: any) => Promise<void>;
  notes: any[];
  fetchNotes: (projectId?: number) => Promise<void>;
  createNote: (data: any) => Promise<number>;
  deleteNote: (id: any) => Promise<void>;
  updateDocument: (id: any, data: any) => Promise<void>;
  timeExtensions: any[];
  loadingTimeExtensions: boolean;
  fetchTimeExtensions: (projectId?: number) => Promise<void>;
  createTimeExtension: (data: any) => Promise<number>;
  updateTimeExtension: (id: any, data: any) => Promise<void>;
  deleteTimeExtension: (id: any) => Promise<void>;
  createOrg: (data: any) => Promise<number>;
  updateOrg: (id: any, data: any) => Promise<void>;
  deleteOrg: (id: any) => Promise<void>;
  createDisaster: (data: any) => Promise<number>;
  updateDisaster: (id: any, data: any) => Promise<void>;
  deleteDisaster: (id: any) => Promise<void>;
}

export const usePAGrantsStore = create<PAGrantsState>((set, get) => ({
  projects: [], projectsTotal: 0, projectsLoading: false, projectsPage: 1,
  projectsSort: { field: 'created_at', dir: 'desc' },
  activeDisasterId: null,
  setActiveDisasterId: (id) => { set({ activeDisasterId: id, projectsPage: 1 }); get().fetchProjects(); },
  projectsFilters: {},

  organizations: [], orgsLoading: false,
  disasters: [],
  reimbursements: [], rfrsTotal: 0, rfrsLoading: false,

  fetchProjects: async (opts) => {
    set({ projectsLoading: true });
    try {
      const { projectsPage, projectsSort } = get();
      const res = await queryRecords<Record<string, unknown>>(TABLES.paProjects, {
        limit: 200,
        offset: (projectsPage - 1) * 200,
        sort: { field: projectsSort.field, direction: projectsSort.dir },
        ...opts,
      });
      set({ projects: res.records.map(mapProject), projectsTotal: res.total });
    } catch (e) { console.error('[PAGrants] fetchProjects', e); }
    set({ projectsLoading: false });
  },

  setProjectsPage: (page) => { set({ projectsPage: page }); get().fetchProjects(); },
  setProjectsSort: (field, dir) => { set({ projectsSort: { field, dir }, projectsPage: 1 }); get().fetchProjects(); },

  fetchOrganizations: async () => {
    set({ orgsLoading: true, loadingOrgs: true });
    try {
      // Small table — fetch all
      const res = await fetchRecords<Record<string, unknown>>(TABLES.paOrganizations, { limit: 500 });
      set({ organizations: res.records.map(mapOrg) });
    } catch (e) { console.error('[PAGrants] fetchOrganizations', e); }
    set({ orgsLoading: false, loadingOrgs: false });
  },

  fetchDisasters: async () => {
    set({ loadingDisasters: true });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.paDisasters, { limit: 100 });
      set({ disasters: res.records.map(mapDisaster) });
    } catch (e) { console.error('[PAGrants] fetchDisasters', e); }
    finally { set({ loadingDisasters: false }); }
  },

  fetchReimbursements: async (opts) => {
    set({ rfrsLoading: true });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.paReimbursementRequests, {
        limit: 50, sort: { field: 'created_at', direction: 'desc' }, ...opts,
      });
      const mapped = res.records.map(mapRFR);
      set({ reimbursements: mapped, reimbursementRequests: mapped, rfrsTotal: res.total });
    } catch (e) { console.error('[PAGrants] fetchReimbursements', e); }
    set({ rfrsLoading: false });
  },

  createReimbursement: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId !== undefined) p[FR.projectId] = data.projectId;
      if (data.organizationId !== undefined) p[FR.organizationId] = data.organizationId;
      if (data.requestNumber) p[FR.requestNumber] = data.requestNumber;
      if (data.requestedAmount !== undefined) p[FR.requestedAmount] = data.requestedAmount;
      if (data.federalShare !== undefined) p[FR.federalShare] = data.federalShare;
      if (data.applicantMatch !== undefined) p[FR.applicantMatch] = data.applicantMatch;
      if (data.status) p[FR.status] = data.status;
      if (data.costBreakdown !== undefined) p[FR.costBreakdown] = data.costBreakdown;
      if (data.submittedBy) p[FR.submittedBy] = data.submittedBy;
      if (data.notes) p[FR.reviewerNotes] = data.notes;
      const r = await createRecord<any>(TABLES.paReimbursementRequests, p);
      await get().fetchReimbursements(); return (r.record_id ?? r.id ?? 0) as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateReimbursement: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.status !== undefined) p[FR.status] = data.status;
      if (data.approvedAmount !== undefined) p[FR.approvedAmount] = data.approvedAmount;
      if (data.reviewerNotes !== undefined) p[FR.reviewerNotes] = data.reviewerNotes;
      if (data.notes !== undefined) p[FR.reviewerNotes] = data.notes;
      if (data.paymentRef !== undefined) p[FR.paymentRef] = data.paymentRef;
      if (data.reviewedBy !== undefined) p[FR.reviewedBy] = data.reviewedBy;
      if (data.reviewedAt !== undefined) p[FR.reviewedAt] = data.reviewedAt;
      if (data.submittedBy !== undefined) p[FR.submittedBy] = data.submittedBy;
      if (data.submittedAt !== undefined) p[FR.submittedAt] = data.submittedAt;
      if (data.costBreakdown !== undefined) p[FR.costBreakdown] = data.costBreakdown;
      if (data.requestedAmount !== undefined) p[FR.requestedAmount] = data.requestedAmount;
      if (data.federalShare !== undefined) p[FR.federalShare] = data.federalShare;
      await updateRecord(TABLES.paReimbursementRequests, id, p);
      await get().fetchReimbursements();
    } catch (e) { console.error('[PA]', e); }
  },
  deleteReimbursement: async (id: any) => {
    try { await deleteRecord(TABLES.paReimbursementRequests, id); await get().fetchReimbursements(); }
    catch (e) { console.error('[PA]', e); }
  },

  createProject: async (data) => {
    try {
      const res = await createRecord(TABLES.paProjects, projectToPayload(data));
      await get().fetchProjects();
      return res as unknown as PAProject;
    } catch (e) { console.error('[PAGrants] createProject', e); return null; }
  },

  updateProject: async (id, data) => {
    try {
      await updateRecord(TABLES.paProjects, id, projectToPayload(data));
      await get().fetchProjects();
    } catch (e) { console.error('[PAGrants] updateProject', e); }
  },

  deleteProject: async (id) => {
    try {
      await deleteRecord(TABLES.paProjects, id);
      await get().fetchProjects();
    } catch (e) { console.error('[PAGrants] deleteProject', e); }
  },

  createOrganization: async (data) => {
    try {
      const res = await createRecord(TABLES.paOrganizations, orgToPayload(data));
      await get().fetchOrganizations();
      return res as unknown as PAOrganization;
    } catch (e) { console.error('[PAGrants] createOrganization', e); return null; }
  },

  updateOrganization: async (id, data) => {
    try {
      await updateRecord(TABLES.paOrganizations, id, orgToPayload(data));
      await get().fetchOrganizations();
    } catch (e) { console.error('[PAGrants] updateOrganization', e); }
  },

  // V1 compat aliases
  fetchAll: async () => {
    await Promise.all([
      get().fetchProjects(),
      get().fetchOrganizations(),
      get().fetchDisasters(),
      get().fetchReimbursements(),
    ]);
  },
  loadingProjects: false,
  loadingCosts: false,
  loadingDisasters: false,
  loadingOrgs: false,
  reimbursementRequests: [] as any[],
  damageItems: [] as any[], loadingDamage: false,
  fetchDamageItems: async (disasterId?: number) => {
    try {
      const filters = disasterId
        ? [{ field: FDI.disasterId, operator: 'eq' as const, value: disasterId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paDamageInventory, {
        filters, limit: 500, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ damageItems: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, disasterId: row[FDI.disasterId], organizationId: row[FDI.organizationId],
        projectId: row[FDI.projectId], siteName: row[FDI.siteName] || '', address: row[FDI.address] || '',
        city: row[FDI.city] || '', state: row[FDI.state] || '', zip: row[FDI.zip] || '',
        latitude: row[FDI.latitude] ? Number(row[FDI.latitude]) : null,
        longitude: row[FDI.longitude] ? Number(row[FDI.longitude]) : null,
        damageCategory: row[FDI.damageCategory] || '', severity: row[FDI.severity] || '',
        description: row[FDI.description] || '', estimatedCost: Number(row[FDI.estimatedCost]) || 0,
        status: row[FDI.status] || 'identified',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createDamageItem: async (data: any) => {
    try {
      const p: any = {};
      if (data.disasterId) p[FDI.disasterId] = data.disasterId;
      if (data.organizationId) p[FDI.organizationId] = data.organizationId;
      if (data.projectId) p[FDI.projectId] = data.projectId;
      if (data.siteName) p[FDI.siteName] = data.siteName;
      if (data.address) p[FDI.address] = data.address;
      if (data.city) p[FDI.city] = data.city;
      if (data.state) p[FDI.state] = data.state;
      if (data.damageCategory) p[FDI.damageCategory] = data.damageCategory;
      if (data.severity) p[FDI.severity] = data.severity;
      if (data.description) p[FDI.description] = data.description;
      if (data.estimatedCost !== undefined) p[FDI.estimatedCost] = data.estimatedCost;
      if (data.status) p[FDI.status] = data.status;
      const r = await createRecord<any>(TABLES.paDamageInventory, p);
      await get().fetchDamageItems(); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateDamageItem: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.siteName !== undefined) p[FDI.siteName] = data.siteName;
      if (data.severity !== undefined) p[FDI.severity] = data.severity;
      if (data.description !== undefined) p[FDI.description] = data.description;
      if (data.estimatedCost !== undefined) p[FDI.estimatedCost] = data.estimatedCost;
      if (data.status !== undefined) p[FDI.status] = data.status;
      if (data.projectId !== undefined) p[FDI.projectId] = data.projectId;
      await updateRecord(TABLES.paDamageInventory, id, p); await get().fetchDamageItems();
    } catch (e) { console.error('[PA]', e); }
  },
  deleteDamageItem: async (id: any) => {
    try { await deleteRecord(TABLES.paDamageInventory, id); await get().fetchDamageItems(); }
    catch (e) { console.error('[PA]', e); }
  },
  projectCosts: [] as any[],
  fetchProjectCosts: async (projectId?: number) => {
    set({ loadingCosts: true });
    try {
      const filters = projectId
        ? [{ field: FPC.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paProjectCosts, {
        filters, limit: 500, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ projectCosts: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FPC.projectId]), organizationId: extractRef(row[FPC.organizationId]),
        costType: row[FPC.costType] || '', description: row[FPC.description] || '',
        quantity: Number(row[FPC.quantity]) || 0, unitCost: Number(row[FPC.unitCost]) || 0,
        totalCost: Number(row[FPC.totalCost]) || 0, unit: row[FPC.unit] || '',
        category: row[FPC.category] || '', notes: row[FPC.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
    finally { set({ loadingCosts: false }); }
  },
  createProjectCost: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FPC.projectId] = data.projectId;
      if (data.organizationId) p[FPC.organizationId] = data.organizationId;
      if (data.costType) p[FPC.costType] = data.costType;
      if (data.description) p[FPC.description] = data.description;
      if (data.quantity !== undefined) p[FPC.quantity] = data.quantity;
      if (data.unitCost !== undefined) p[FPC.unitCost] = data.unitCost;
      if (data.totalCost !== undefined) p[FPC.totalCost] = data.totalCost;
      if (data.unit) p[FPC.unit] = data.unit;
      if (data.category) p[FPC.category] = data.category;
      if (data.notes) p[FPC.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paProjectCosts, p);
      await get().fetchProjectCosts(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateProjectCost: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.description !== undefined) p[FPC.description] = data.description;
      if (data.quantity !== undefined) p[FPC.quantity] = data.quantity;
      if (data.unitCost !== undefined) p[FPC.unitCost] = data.unitCost;
      if (data.totalCost !== undefined) p[FPC.totalCost] = data.totalCost;
      if (data.category !== undefined) p[FPC.category] = data.category;
      if (data.notes !== undefined) p[FPC.notes] = data.notes;
      // Refresh scoped to the project if we can determine it
      const existing = get().projectCosts.find((c: any) => c.id === id);
      await updateRecord(TABLES.paProjectCosts, id, p);
      await get().fetchProjectCosts(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteProjectCost: async (id: any) => {
    try {
      const existing = get().projectCosts.find((c: any) => c.id === id);
      await deleteRecord(TABLES.paProjectCosts, id);
      await get().fetchProjectCosts(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  documents: [] as any[], loadingDocs: false,
  fetchDocuments: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FDoc.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paDocuments, {
        filters, limit: 200, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ documents: r.records.map((row: any) => {
        const title = row[FDoc.title] || '';
        // documentId: prefer DOCID meta marker, fall back to _attachments metadata (same pattern as fileLibraryStore)
        const rawNotes = String(row[FDoc.notes] || '');
        const docIdMatch = rawNotes.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
        const attachments = row._attachments as Record<string, any> | undefined;
        const firstAttachDoc = attachments?.[FDoc.file]?.documents?.[0];
        const fileId = docIdMatch ? docIdMatch[1] : (firstAttachDoc?.id ?? '');
        const cleanNotes = rawNotes.replace(/\|\|\|DOCID\|\|\|.*/, '').trim();
        return {
          id: row.record_id, Rid: row.record_id,
          projectId: extractRef(row[FDoc.projectId]), organizationId: extractRef(row[FDoc.organizationId]),
          title, documentName: title, documentType: row[FDoc.documentType] || '',
          tags: row[FDoc.tags] || '', uploadedBy: row[FDoc.uploadedBy] || '',
          fileId,
          notes: cleanNotes,
        };
      }) });
    } catch (e) { console.error('[PA]', e); }
  },
  createDocument: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FDoc.projectId] = data.projectId;
      if (data.organizationId) p[FDoc.organizationId] = data.organizationId;
      if (data.title) p[FDoc.title] = data.title;
      if (data.documentType) p[FDoc.documentType] = data.documentType;
      if (data.uploadedBy) p[FDoc.uploadedBy] = data.uploadedBy;
      if (data.notes) p[FDoc.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paDocuments, p);
      await get().fetchDocuments(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  deleteDocument: async (id: any) => {
    try {
      const existing = get().documents.find((d: any) => d.id === id);
      await deleteRecord(TABLES.paDocuments, id);
      await get().fetchDocuments(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  uploadDocumentFile: async (id: number, file: File, currentNotes?: string) => {
    const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.paDocuments);
    // Store documentId in notes with DOCID marker — ATTACHMENT field writes are silently dropped in V2.
    // documentId is required for download-url; attachmentId is the linking reference for the file field.
    const notesWithDocId = `${currentNotes || ''}|||DOCID|||${documentId}|||ATTACHID|||${attachmentId}|||`;
    await updateRecord(TABLES.paDocuments, id, {
      [FDoc.file]: attachmentId,
      [FDoc.notes]: notesWithDocId,
    });
    const existing = get().documents.find((d: any) => d.id === id);
    await get().fetchDocuments(existing?.projectId);
  },
  getDocumentDownloadUrl: async (documentId: string) => {
    return getV2FileDownloadUrl(documentId);
  },
  laborRecords: [] as any[],
  fetchLabor: async (projectId?: number) => {
    try {
      const FL = FIELDS.forceAccountLabor;
      const filters = projectId
        ? [{ field: FL.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.forceAccountLabor, {
        filters, limit: 500, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ laborRecords: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id,
        projectId: row[FL.projectId],
        incidentId: row[FL.incidentId],
        employeeId: row[FL.employeeId],
        employeeName: row[FL.employeeName] || '',
        activityDescription: row[FL.activityDescription] || '',
        hoursRegular: Number(row[FL.hoursRegular]) || 0,
        hoursOvertime: Number(row[FL.hoursOvertime]) || 0,
        totalCost: Number(row[FL.totalCost]) || 0,
        status: row[FL.status] || 'draft',
        workDate: row[FL.workDate] || '',
        category: row[FL.category] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createLabor: async (data: any) => {
    try {
      const FL = FIELDS.forceAccountLabor;
      const p: any = {};
      if (data.incidentId !== undefined) p[FL.incidentId] = data.incidentId;
      if (data.projectId !== undefined) p[FL.projectId] = data.projectId;
      if (data.employeeName) p[FL.employeeName] = data.employeeName;
      if (data.employeeId) p[FL.employeeId] = data.employeeId;
      if (data.workDate) p[FL.workDate] = data.workDate;
      if (data.hoursRegular !== undefined) p[FL.hoursRegular] = data.hoursRegular;
      if (data.hoursOvertime !== undefined) p[FL.hoursOvertime] = data.hoursOvertime;
      if (data.rateRegular !== undefined) p[FL.rateRegular] = data.rateRegular;
      if (data.rateOvertime !== undefined) p[FL.rateOvertime] = data.rateOvertime;
      if (data.totalCost !== undefined) p[FL.totalCost] = data.totalCost;
      if (data.category) p[FL.category] = data.category;
      if (data.activityDescription) p[FL.activityDescription] = data.activityDescription;
      if (data.status) p[FL.status] = data.status;
      if (data.notes) p[FL.notes] = data.notes;
      const r = await createRecord<any>(TABLES.forceAccountLabor, p);
      await get().fetchLabor(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateLabor: async (id: any, data: any) => {
    try {
      const FL = FIELDS.forceAccountLabor;
      const p: any = {};
      if (data.hoursRegular !== undefined) p[FL.hoursRegular] = data.hoursRegular;
      if (data.hoursOvertime !== undefined) p[FL.hoursOvertime] = data.hoursOvertime;
      if (data.rateRegular !== undefined) p[FL.rateRegular] = data.rateRegular;
      if (data.totalCost !== undefined) p[FL.totalCost] = data.totalCost;
      if (data.status !== undefined) p[FL.status] = data.status;
      if (data.approvedRegHours !== undefined) p[FL.approvedRegHours] = data.approvedRegHours;
      if (data.approvedOTHours !== undefined) p[FL.approvedOTHours] = data.approvedOTHours;
      if (data.approvedCost !== undefined) p[FL.approvedCost] = data.approvedCost;
      if (data.reviewerNotes !== undefined) p[FL.reviewerNotes] = data.reviewerNotes;
      if (data.projectId !== undefined) p[FL.projectId] = data.projectId;
      if (data.notes !== undefined) p[FL.notes] = data.notes;
      const existing = get().laborRecords.find((l: any) => l.id === id);
      await updateRecord(TABLES.forceAccountLabor, id, p);
      await get().fetchLabor(existing?.projectId ?? data.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteLabor: async (id: any) => {
    try {
      const existing = get().laborRecords.find((l: any) => l.id === id);
      await deleteRecord(TABLES.forceAccountLabor, id);
      await get().fetchLabor(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  equipRecords: [] as any[],
  fetchEquipment: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FEq.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paForceAccountEquipment, {
        filters, limit: 500, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ equipRecords: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FEq.projectId]), organizationId: extractRef(row[FEq.organizationId]),
        equipmentName: row[FEq.equipmentName] || '', equipmentType: row[FEq.equipmentType] || '',
        size: row[FEq.size] || '', hoursUsed: Number(row[FEq.hoursUsed]) || 0,
        ratePerHour: Number(row[FEq.ratePerHour]) || 0, totalCost: Number(row[FEq.totalCost]) || 0,
        workDate: row[FEq.workDate] || '', operator: row[FEq.operator] || '', notes: row[FEq.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createEquip: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FEq.projectId] = data.projectId;
      if (data.equipmentName) p[FEq.equipmentName] = data.equipmentName;
      if (data.equipmentType) p[FEq.equipmentType] = data.equipmentType;
      if (data.hoursUsed !== undefined) p[FEq.hoursUsed] = data.hoursUsed;
      if (data.ratePerHour !== undefined) p[FEq.ratePerHour] = data.ratePerHour;
      if (data.totalCost !== undefined) p[FEq.totalCost] = data.totalCost;
      if (data.workDate) p[FEq.workDate] = data.workDate;
      if (data.operator) p[FEq.operator] = data.operator;
      if (data.notes) p[FEq.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paForceAccountEquipment, p);
      await get().fetchEquipment(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateEquip: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.hoursUsed !== undefined) p[FEq.hoursUsed] = data.hoursUsed;
      if (data.ratePerHour !== undefined) p[FEq.ratePerHour] = data.ratePerHour;
      if (data.totalCost !== undefined) p[FEq.totalCost] = data.totalCost;
      if (data.notes !== undefined) p[FEq.notes] = data.notes;
      const existing = get().equipRecords.find((e: any) => e.id === id);
      await updateRecord(TABLES.paForceAccountEquipment, id, p);
      await get().fetchEquipment(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteEquip: async (id: any) => {
    try {
      const existing = get().equipRecords.find((e: any) => e.id === id);
      await deleteRecord(TABLES.paForceAccountEquipment, id);
      await get().fetchEquipment(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  materials: [] as any[],
  fetchMaterials: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FMat.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paMaterials, {
        filters, limit: 500, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ materials: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FMat.projectId]), organizationId: extractRef(row[FMat.organizationId]),
        description: row[FMat.description] || '', vendor: row[FMat.vendor] || '',
        quantity: Number(row[FMat.quantity]) || 0, unitCost: Number(row[FMat.unitCost]) || 0,
        totalCost: Number(row[FMat.totalCost]) || 0, purchaseDate: row[FMat.purchaseDate] || '',
        invoiceNumber: row[FMat.invoiceNumber] || '', notes: row[FMat.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createMaterial: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FMat.projectId] = data.projectId;
      if (data.description) p[FMat.description] = data.description;
      if (data.vendor) p[FMat.vendor] = data.vendor;
      if (data.quantity !== undefined) p[FMat.quantity] = data.quantity;
      if (data.unitCost !== undefined) p[FMat.unitCost] = data.unitCost;
      if (data.totalCost !== undefined) p[FMat.totalCost] = data.totalCost;
      if (data.purchaseDate) p[FMat.purchaseDate] = data.purchaseDate;
      if (data.invoiceNumber) p[FMat.invoiceNumber] = data.invoiceNumber;
      if (data.notes) p[FMat.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paMaterials, p);
      await get().fetchMaterials(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateMaterial: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.description !== undefined) p[FMat.description] = data.description;
      if (data.quantity !== undefined) p[FMat.quantity] = data.quantity;
      if (data.unitCost !== undefined) p[FMat.unitCost] = data.unitCost;
      if (data.totalCost !== undefined) p[FMat.totalCost] = data.totalCost;
      if (data.notes !== undefined) p[FMat.notes] = data.notes;
      const existing = get().materials.find((m: any) => m.id === id);
      await updateRecord(TABLES.paMaterials, id, p);
      await get().fetchMaterials(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteMaterial: async (id: any) => {
    try {
      const existing = get().materials.find((m: any) => m.id === id);
      await deleteRecord(TABLES.paMaterials, id);
      await get().fetchMaterials(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  contracts: [] as any[],
  fetchContracts: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FCon.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paContracts, {
        filters, limit: 200, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ contracts: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FCon.projectId]), organizationId: extractRef(row[FCon.organizationId]),
        contractorName: row[FCon.contractorName] || '', contractNumber: row[FCon.contractNumber] || '',
        description: row[FCon.description] || '', contractAmount: Number(row[FCon.contractAmount]) || 0,
        procurementMethod: row[FCon.procurementMethod] || '',
        startDate: row[FCon.startDate] || '', endDate: row[FCon.endDate] || '',
        status: row[FCon.status] || 'active', notes: row[FCon.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createContract: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FCon.projectId] = data.projectId;
      if (data.contractorName) p[FCon.contractorName] = data.contractorName;
      if (data.contractNumber) p[FCon.contractNumber] = data.contractNumber;
      if (data.description) p[FCon.description] = data.description;
      if (data.contractAmount !== undefined) p[FCon.contractAmount] = data.contractAmount;
      if (data.procurementMethod) p[FCon.procurementMethod] = data.procurementMethod;
      if (data.startDate) p[FCon.startDate] = data.startDate;
      if (data.endDate) p[FCon.endDate] = data.endDate;
      if (data.status) p[FCon.status] = data.status;
      if (data.notes) p[FCon.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paContracts, p);
      await get().fetchContracts(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateContract: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.contractorName !== undefined) p[FCon.contractorName] = data.contractorName;
      if (data.contractAmount !== undefined) p[FCon.contractAmount] = data.contractAmount;
      if (data.status !== undefined) p[FCon.status] = data.status;
      if (data.notes !== undefined) p[FCon.notes] = data.notes;
      const existing = get().contracts.find((c: any) => c.id === id);
      await updateRecord(TABLES.paContracts, id, p);
      await get().fetchContracts(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteContract: async (id: any) => {
    try {
      const existing = get().contracts.find((c: any) => c.id === id);
      await deleteRecord(TABLES.paContracts, id);
      await get().fetchContracts(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  rentedEquip: [] as any[],
  fetchRented: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FRent.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paRentedEquipment, {
        filters, limit: 200, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ rentedEquip: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: row[FRent.projectId], organizationId: row[FRent.organizationId],
        equipmentName: row[FRent.equipmentName] || '', vendor: row[FRent.vendor] || '',
        rentalRate: Number(row[FRent.rentalRate]) || 0, rentalPeriod: row[FRent.rentalPeriod] || '',
        totalCost: Number(row[FRent.totalCost]) || 0,
        startDate: row[FRent.startDate] || '', endDate: row[FRent.endDate] || '',
        notes: row[FRent.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createRented: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FRent.projectId] = data.projectId;
      if (data.equipmentName) p[FRent.equipmentName] = data.equipmentName;
      if (data.vendor) p[FRent.vendor] = data.vendor;
      if (data.rentalRate !== undefined) p[FRent.rentalRate] = data.rentalRate;
      if (data.rentalPeriod) p[FRent.rentalPeriod] = data.rentalPeriod;
      if (data.totalCost !== undefined) p[FRent.totalCost] = data.totalCost;
      if (data.startDate) p[FRent.startDate] = data.startDate;
      if (data.endDate) p[FRent.endDate] = data.endDate;
      if (data.notes) p[FRent.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paRentedEquipment, p);
      await get().fetchRented(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateRented: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.equipmentName !== undefined) p[FRent.equipmentName] = data.equipmentName;
      if (data.rentalRate !== undefined) p[FRent.rentalRate] = data.rentalRate;
      if (data.totalCost !== undefined) p[FRent.totalCost] = data.totalCost;
      if (data.notes !== undefined) p[FRent.notes] = data.notes;
      const existing = get().rentedEquip.find((r: any) => r.id === id);
      await updateRecord(TABLES.paRentedEquipment, id, p);
      await get().fetchRented(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteRented: async (id: any) => {
    try {
      const existing = get().rentedEquip.find((r: any) => r.id === id);
      await deleteRecord(TABLES.paRentedEquipment, id);
      await get().fetchRented(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  donatedResources: [] as any[],
  fetchDonated: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FDon.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paDonatedResources, {
        filters, limit: 200, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ donatedResources: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: row[FDon.projectId], organizationId: row[FDon.organizationId],
        donorName: row[FDon.donorName] || '', resourceType: row[FDon.resourceType] || '',
        description: row[FDon.description] || '', estimatedValue: Number(row[FDon.estimatedValue]) || 0,
        hoursContributed: Number(row[FDon.hoursContributed]) || 0,
        donationDate: row[FDon.donationDate] || '', notes: row[FDon.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createDonated: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FDon.projectId] = data.projectId;
      if (data.donorName) p[FDon.donorName] = data.donorName;
      if (data.resourceType) p[FDon.resourceType] = data.resourceType;
      if (data.description) p[FDon.description] = data.description;
      if (data.estimatedValue !== undefined) p[FDon.estimatedValue] = data.estimatedValue;
      if (data.hoursContributed !== undefined) p[FDon.hoursContributed] = data.hoursContributed;
      if (data.donationDate) p[FDon.donationDate] = data.donationDate;
      if (data.notes) p[FDon.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paDonatedResources, p);
      await get().fetchDonated(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateDonated: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.estimatedValue !== undefined) p[FDon.estimatedValue] = data.estimatedValue;
      if (data.hoursContributed !== undefined) p[FDon.hoursContributed] = data.hoursContributed;
      if (data.notes !== undefined) p[FDon.notes] = data.notes;
      const existing = get().donatedResources.find((d: any) => d.id === id);
      await updateRecord(TABLES.paDonatedResources, id, p);
      await get().fetchDonated(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteDonated: async (id: any) => {
    try {
      const existing = get().donatedResources.find((d: any) => d.id === id);
      await deleteRecord(TABLES.paDonatedResources, id);
      await get().fetchDonated(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  insurance: [] as any[],
  fetchInsurance: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FIns.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paInsurance, {
        filters, limit: 100, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ insurance: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FIns.projectId]),
        carrier: row[FIns.carrier] || '', policyNumber: row[FIns.policyNumber] || '',
        coverageType: row[FIns.coverageType] || '', claimNumber: row[FIns.claimNumber] || '',
        settlementAmount: Number(row[FIns.settlementAmount]) || 0,
        deductible: Number(row[FIns.deductible]) || 0,
        femaDeduction: Number(row[FIns.femaDeduction]) || 0,
        status: row[FIns.status] || 'pending', notes: row[FIns.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createInsurance: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FIns.projectId] = data.projectId;
      if (data.carrier) p[FIns.carrier] = data.carrier;
      if (data.policyNumber) p[FIns.policyNumber] = data.policyNumber;
      if (data.coverageType) p[FIns.coverageType] = data.coverageType;
      if (data.claimNumber) p[FIns.claimNumber] = data.claimNumber;
      if (data.settlementAmount !== undefined) p[FIns.settlementAmount] = data.settlementAmount;
      if (data.deductible !== undefined) p[FIns.deductible] = data.deductible;
      if (data.femaDeduction !== undefined) p[FIns.femaDeduction] = data.femaDeduction;
      if (data.status) p[FIns.status] = data.status;
      if (data.notes) p[FIns.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paInsurance, p);
      await get().fetchInsurance(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateInsurance: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.settlementAmount !== undefined) p[FIns.settlementAmount] = data.settlementAmount;
      if (data.femaDeduction !== undefined) p[FIns.femaDeduction] = data.femaDeduction;
      if (data.status !== undefined) p[FIns.status] = data.status;
      if (data.notes !== undefined) p[FIns.notes] = data.notes;
      if (data.claimNumber !== undefined) p[FIns.claimNumber] = data.claimNumber;
      const existing = get().insurance.find((ins: any) => ins.id === id);
      await updateRecord(TABLES.paInsurance, id, p);
      await get().fetchInsurance(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteInsurance: async (id: any) => {
    try {
      const existing = get().insurance.find((ins: any) => ins.id === id);
      await deleteRecord(TABLES.paInsurance, id);
      await get().fetchInsurance(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  compliance: [] as any[],
  fetchCompliance: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FComp.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paCompliance, {
        filters, limit: 100, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ compliance: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FComp.projectId]),
        nepaRequired: row[FComp.nepaRequired] === true || row[FComp.nepaRequired] === 'true',
        nepaStatus: row[FComp.nepaStatus] || '',
        section106Required: row[FComp.section106Required] === true || row[FComp.section106Required] === 'true',
        section106Status: row[FComp.section106Status] || '',
        floodplainReview: row[FComp.floodplainReview] || '',
        floodInsurance: row[FComp.floodInsurance] || '',
        hazmatReview: row[FComp.hazmatReview] || '',
        esaReview: row[FComp.esaReview] || '',
        wetlandsReview: row[FComp.wetlandsReview] || '',
        notes: row[FComp.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createCompliance: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FComp.projectId] = data.projectId;
      Object.keys(data).forEach(k => {
        if (k !== 'projectId' && (FComp as any)[k]) p[(FComp as any)[k]] = data[k];
      });
      const r = await createRecord<any>(TABLES.paCompliance, p);
      await get().fetchCompliance(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateCompliance: async (id: any, data: any) => {
    try {
      const p: any = {};
      Object.keys(data).forEach(k => { if ((FComp as any)[k]) p[(FComp as any)[k]] = data[k]; });
      const existing = get().compliance.find((c: any) => c.id === id);
      await updateRecord(TABLES.paCompliance, id, p);
      await get().fetchCompliance(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteCompliance: async (id: any) => {
    try {
      const existing = get().compliance.find((c: any) => c.id === id);
      await deleteRecord(TABLES.paCompliance, id);
      await get().fetchCompliance(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  timelineEvents: [] as any[],
  fetchTimeline: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FTL.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paTimelineEvents, {
        filters, limit: 500, sort: { field: FTL.eventDate, direction: 'asc' },
      });
      set({ timelineEvents: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FTL.projectId]),
        eventType: row[FTL.eventType] || '', title: row[FTL.title] || '',
        description: row[FTL.description] || '', eventDate: row[FTL.eventDate] || '',
        createdBy: row[FTL.createdBy] || '', status: row[FTL.status] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createTimeline: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FTL.projectId] = data.projectId;
      if (data.eventType) p[FTL.eventType] = data.eventType;
      // title is required by V2 — fall back to truncated description if not supplied
      const title = data.title || (data.description ? String(data.description).substring(0, 80) : 'Event');
      p[FTL.title] = title;
      if (data.description) p[FTL.description] = data.description;
      // V2 eventDate is timestamp with time zone — must be full ISO string, not bare YYYY-MM-DD
      if (data.eventDate) {
        const d = data.eventDate;
        // If it's already a full ISO timestamp, use as-is; otherwise append midnight UTC
        p[FTL.eventDate] = /^\d{4}-\d{2}-\d{2}$/.test(String(d))
          ? `${d}T00:00:00.000Z`
          : String(d);
      }
      // accept both createdBy and creatorId
      const createdBy = data.createdBy || data.creatorId || '';
      if (createdBy) p[FTL.createdBy] = createdBy;
      if (data.status) p[FTL.status] = data.status;
      const r = await createRecord<any>(TABLES.paTimelineEvents, p);
      await get().fetchTimeline(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  notes: [] as any[],
  fetchNotes: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FNote.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paNotes, {
        filters, limit: 200, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ notes: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FNote.projectId]),
        authorName: row[FNote.authorName] || '', authorEmail: row[FNote.authorEmail] || '',
        content: row[FNote.content] || '', noteType: row[FNote.noteType] || 'general',
        datecreated: row.created_at || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createNote: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FNote.projectId] = data.projectId;
      if (data.authorName) p[FNote.authorName] = data.authorName;
      if (data.authorEmail) p[FNote.authorEmail] = data.authorEmail;
      if (data.content) p[FNote.content] = data.content;
      if (data.noteType) p[FNote.noteType] = data.noteType;
      const r = await createRecord<any>(TABLES.paNotes, p);
      await get().fetchNotes(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  timeExtensions: [] as any[],
  loadingTimeExtensions: false,
  fetchTimeExtensions: async (projectId?: number) => {
    try {
      const filters = projectId
        ? [{ field: FTE.projectId, operator: 'eq' as const, value: projectId }]
        : undefined;
      const r = await queryRecords<any>(TABLES.paTimeExtensions, {
        filters, limit: 200, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ timeExtensions: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, projectId: extractRef(row[FTE.projectId]), organizationId: extractRef(row[FTE.organizationId]),
        disasterId: row[FTE.disasterId], requestDate: row[FTE.requestDate] || '',
        currentDeadline: row[FTE.currentDeadline] || '',
        requestedNewDeadline: row[FTE.requestedNewDeadline] || '',
        extensionMonths: String(row[FTE.extensionMonths] || '6'),
        recipientName: row[FTE.recipientName] || '',
        recipientTitle: row[FTE.recipientTitle] || '',
        recipientOrg: row[FTE.recipientOrg] || 'FEMA',
        recipientAddress: row[FTE.recipientAddress] || '',
        justification: row[FTE.justification] || '',
        supportingCircumstances: row[FTE.supportingCircumstances] || '',
        letterBody: row[FTE.letterBody] || '', letterContent: row[FTE.letterContent] || '',
        status: row[FTE.status] || 'draft',
        approvedBy: row[FTE.approvedBy] || '', approvalDate: row[FTE.approvalDate] || '',
        approvedNewDeadline: row[FTE.approvedNewDeadline] || '',
        createdBy: row[FTE.createdBy] || '', submittedBy: row[FTE.submittedBy] || '',
        submittedAt: row[FTE.submittedAt] || '', notes: row[FTE.notes] || '',
      })) });
    } catch (e) { console.error('[PA]', e); }
  },
  createTimeExtension: async (data: any) => {
    try {
      const p: any = {};
      if (data.projectId) p[FTE.projectId] = data.projectId;
      if (data.organizationId) p[FTE.organizationId] = data.organizationId;
      if (data.disasterId) p[FTE.disasterId] = data.disasterId;
      if (data.extensionMonths !== undefined) p[FTE.extensionMonths] = data.extensionMonths;
      if (data.requestDate) p[FTE.requestDate] = data.requestDate;
      if (data.currentDeadline) p[FTE.currentDeadline] = data.currentDeadline;
      if (data.requestedNewDeadline) p[FTE.requestedNewDeadline] = data.requestedNewDeadline;
      if (data.recipientName !== undefined) p[FTE.recipientName] = data.recipientName;
      if (data.recipientTitle !== undefined) p[FTE.recipientTitle] = data.recipientTitle;
      if (data.recipientOrg !== undefined) p[FTE.recipientOrg] = data.recipientOrg;
      if (data.recipientAddress !== undefined) p[FTE.recipientAddress] = data.recipientAddress;
      if (data.justification) p[FTE.justification] = data.justification;
      if (data.supportingCircumstances !== undefined) p[FTE.supportingCircumstances] = data.supportingCircumstances;
      if (data.letterContent !== undefined) p[FTE.letterContent] = data.letterContent;
      if (data.status) p[FTE.status] = data.status;
      if (data.notes !== undefined) p[FTE.notes] = data.notes;
      if (data.createdBy) p[FTE.createdBy] = data.createdBy;
      const r = await createRecord<any>(TABLES.paTimeExtensions, p);
      await get().fetchTimeExtensions(data.projectId); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateTimeExtension: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.status !== undefined) p[FTE.status] = data.status;
      if (data.extensionMonths !== undefined) p[FTE.extensionMonths] = data.extensionMonths;
      if (data.requestDate !== undefined) p[FTE.requestDate] = data.requestDate;
      if (data.currentDeadline !== undefined) p[FTE.currentDeadline] = data.currentDeadline;
      if (data.requestedNewDeadline !== undefined) p[FTE.requestedNewDeadline] = data.requestedNewDeadline;
      if (data.recipientName !== undefined) p[FTE.recipientName] = data.recipientName;
      if (data.recipientTitle !== undefined) p[FTE.recipientTitle] = data.recipientTitle;
      if (data.recipientOrg !== undefined) p[FTE.recipientOrg] = data.recipientOrg;
      if (data.recipientAddress !== undefined) p[FTE.recipientAddress] = data.recipientAddress;
      if (data.justification !== undefined) p[FTE.justification] = data.justification;
      if (data.supportingCircumstances !== undefined) p[FTE.supportingCircumstances] = data.supportingCircumstances;
      if (data.letterBody !== undefined) p[FTE.letterBody] = data.letterBody;
      if (data.letterContent !== undefined) p[FTE.letterContent] = data.letterContent;
      if (data.approvedBy !== undefined) p[FTE.approvedBy] = data.approvedBy;
      if (data.approvalDate !== undefined) p[FTE.approvalDate] = data.approvalDate;
      if (data.approvedNewDeadline !== undefined) p[FTE.approvedNewDeadline] = data.approvedNewDeadline;
      if (data.reviewedBy !== undefined) p[FTE.reviewedBy] = data.reviewedBy;
      if (data.reviewedAt !== undefined) p[FTE.reviewedAt] = data.reviewedAt;
      if (data.notes !== undefined) p[FTE.notes] = data.notes;
      if (data.submittedBy !== undefined) p[FTE.submittedBy] = data.submittedBy;
      if (data.submittedAt !== undefined) p[FTE.submittedAt] = data.submittedAt;
      const existing = get().timeExtensions.find((te: any) => te.id === id);
      await updateRecord(TABLES.paTimeExtensions, id, p);
      await get().fetchTimeExtensions(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteTimeExtension: async (id: any) => {
    try {
      const existing = get().timeExtensions.find((te: any) => te.id === id);
      await deleteRecord(TABLES.paTimeExtensions, id);
      await get().fetchTimeExtensions(existing?.projectId);
    }
    catch (e) { console.error('[PA]', e); }
  },
  // Timeline event aliases (pages call createTimelineEvent, updateTimelineEvent, deleteTimelineEvent)
  createTimelineEvent: async (data: any) => get().createTimeline(data),
  updateTimelineEvent: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.eventType !== undefined) p[FTL.eventType] = data.eventType;
      if (data.description !== undefined) p[FTL.description] = data.description;
      if (data.status !== undefined) p[FTL.status] = data.status;
      const existing = get().timelineEvents.find((te: any) => te.id === id);
      await updateRecord(TABLES.paTimelineEvents, id, p);
      await get().fetchTimeline(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  deleteTimelineEvent: async (id: any) => {
    try {
      const existing = get().timelineEvents.find((te: any) => te.id === id);
      await deleteRecord(TABLES.paTimelineEvents, id);
      await get().fetchTimeline(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  // Note aliases
  deleteNote: async (id: any) => {
    try {
      const existing = get().notes.find((n: any) => n.id === id);
      await deleteRecord(TABLES.paNotes, id);
      await get().fetchNotes(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  // Document aliases
  updateDocument: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.title !== undefined) p[FDoc.title] = data.title;
      if (data.documentType !== undefined) p[FDoc.documentType] = data.documentType;
      if (data.tags !== undefined) p[FDoc.tags] = data.tags;
      if (data.notes !== undefined) p[FDoc.notes] = data.notes;
      const existing = get().documents.find((d: any) => d.id === id);
      await updateRecord(TABLES.paDocuments, id, p);
      await get().fetchDocuments(existing?.projectId);
    } catch (e) { console.error('[PA]', e); }
  },
  createOrg: async (data: any) => { const r = await get().createOrganization(data); return (r as any)?.id || 0; },
  updateOrg: async (id: any, data: any) => get().updateOrganization(id, data),
  deleteOrg: async (id: any) => {
    try { await deleteRecord(TABLES.paOrganizations, id); await get().fetchOrganizations(); }
    catch (e) { console.error('[PA]', e); }
  },
  createDisaster: async (data: any) => {
    try {
      const p: any = {};
      if (data.disasterNumber) p[FD.disasterNumber] = data.disasterNumber;
      if (data.disasterName) p[FD.disasterName] = data.disasterName;
      if (data.declarationType) p[FD.declarationType] = data.declarationType;
      if (data.declarationDate) p[FD.declarationDate] = data.declarationDate;
      if (data.incidentId !== undefined) p[FD.incidentId] = data.incidentId;
      if (data.incidentPeriodStart) p[FD.incidentPeriodStart] = data.incidentPeriodStart;
      if (data.incidentPeriodEnd) p[FD.incidentPeriodEnd] = data.incidentPeriodEnd;
      if (data.federalSharePct !== undefined) p[FD.federalSharePct] = data.federalSharePct;
      if (data.status) p[FD.status] = data.status;
      if (data.notes) p[FD.notes] = data.notes;
      const r = await createRecord<any>(TABLES.paDisasters, p);
      await get().fetchDisasters(); return r.record_id as number;
    } catch (e) { console.error('[PA]', e); return 0; }
  },
  updateDisaster: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.disasterName !== undefined) p[FD.disasterName] = data.disasterName;
      if (data.declarationType !== undefined) p[FD.declarationType] = data.declarationType;
      if (data.declarationDate !== undefined) p[FD.declarationDate] = data.declarationDate;
      if (data.incidentPeriodStart !== undefined) p[FD.incidentPeriodStart] = data.incidentPeriodStart;
      if (data.incidentPeriodEnd !== undefined) p[FD.incidentPeriodEnd] = data.incidentPeriodEnd;
      if (data.federalSharePct !== undefined) p[FD.federalSharePct] = data.federalSharePct;
      if (data.status !== undefined) p[FD.status] = data.status;
      if (data.incidentId !== undefined) p[FD.incidentId] = data.incidentId;
      if (data.notes !== undefined) p[FD.notes] = data.notes;
      await updateRecord(TABLES.paDisasters, id, p); await get().fetchDisasters();
    } catch (e) { console.error('[PA]', e); }
  },
  deleteDisaster: async (id: any) => {
    try { await deleteRecord(TABLES.paDisasters, id); await get().fetchDisasters(); }
    catch (e) { console.error('[PA]', e); }
  },
}));
